import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Skeleton,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
  Shield as ShieldIcon,
  Assignment as AssignmentIcon,
  Hub as HubIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { reportsAPI, ReportPayloadDto } from '../services/api';
import { chipSxFromSurface, getLifecycleStatusSurface, getRiskSurface } from '../utils/appearanceTokens';
import { ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import type { PaletteMode } from '@mui/material';

const HEADER_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
  py: 1.5,
  whiteSpace: 'nowrap' as const,
  backgroundColor: 'action.hover',
};

type ReportType =
  | 'inactive_users'
  | 'all_inactive'
  | 'sso_status'
  | 'compliance'
  | 'security'
  | 'application_portfolio'
  | 'delegations';

interface ReportData {
  id: ReportType;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  data: Record<string, unknown>[];
  columns: { key: string; label: string }[];
  lastGenerated: Date;
}

const REPORT_ICONS: Record<string, React.ReactNode> = {
  inactive_users: <PeopleIcon />,
  all_inactive: <WarningIcon />,
  sso_status: <SecurityIcon />,
  compliance: <CheckCircleIcon />,
  security: <ShieldIcon />,
  application_portfolio: <AppsIcon />,
  delegations: <AssignmentIcon />,
};

function statusChipSx(status: string, mode: PaletteMode) {
  return {
    ...chipSxFromSurface(getLifecycleStatusSurface(status, mode)),
    fontWeight: 600,
  };
}

function riskChipSx(level: string, mode: PaletteMode) {
  return {
    ...chipSxFromSurface(getRiskSurface(level, mode)),
    fontWeight: ['Critical', 'High'].includes(level) ? 700 : 600,
  };
}

function exportCSV(report: ReportData) {
  if (!report.data.length) return;
  const headers = report.columns.map(c => c.label).join(',');
  const rows = report.data.map(row =>
    report.columns.map(c => {
      const val = row[c.key];
      const str = typeof val === 'object' ? JSON.stringify(val) : String(val ?? '');
      return `"${str.replace(/"/g, '""')}"`;
    }).join(',')
  );
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${report.id}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function exportPDF(report: ReportData) {
  if (!report.data.length) return;

  const headerRow = `<tr>${report.columns.map(c => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr>`;
  const bodyRows = report.data
    .map(row =>
      `<tr>${report.columns
        .map(c => {
          const val = row[c.key];
          const str = typeof val === 'object' ? JSON.stringify(val) : String(val ?? '');
          return `<td>${escapeHtml(str)}</td>`;
        })
        .join('')}</tr>`
    )
    .join('');

  const title = escapeHtml(report.title);
  const desc = escapeHtml(report.description);
  const slug = `${report.id}_${new Date().toISOString().slice(0, 10)}`;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title>
<style>
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; padding: 24px; color: #111; }
  h1 { font-size: 18px; margin: 0 0 8px; }
  .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
  table { border-collapse: collapse; width: 100%; font-size: 11px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f5f5f5; font-weight: 600; }
  @media print { body { padding: 12px; } }
</style></head><body>
  <h1>${title}</h1>
  <p class="meta">${desc}</p>
  <table><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>
  <script>
    document.title = ${JSON.stringify(slug)};
    window.addEventListener('load', function () { window.print(); });
  <\/script>
</body></html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    URL.revokeObjectURL(url);
    window.alert('Could not open the print window. Allow pop-ups for this site and try again.');
    return;
  }
  w.focus();
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }, 120_000);
}

function mapDtoToReport(dto: ReportPayloadDto): ReportData {
  return {
    id: dto.id as ReportType,
    title: dto.title,
    description: dto.description,
    icon: REPORT_ICONS[dto.id] || <HubIcon />,
    color: dto.color,
    data: dto.data || [],
    columns: dto.columns || [],
    lastGenerated: new Date(dto.lastGenerated),
  };
}

const Reports: React.FC = () => {
  const { colors, resolvedMode } = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [reports, setReports] = useState<ReportData[]>([]);
  const [selected, setSelected] = useState<ReportData | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { reports: payload, warnings: w } = await reportsAPI.getReports();
      setReports((payload || []).map(mapDtoToReport));
      setWarnings(w || []);
    } catch (err: any) {
      console.error('Failed to load report data:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load reports');
      setReports([]);
      setWarnings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = sortListing(reports.filter(r => {
    const matchSearch = !searchTerm || r.title.toLowerCase().includes(searchTerm.toLowerCase()) || r.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || r.id === filterType;
    return matchSearch && matchType;
  }), listingSort, {
    getName: (r) => r.title,
    getDate: (r) => r.lastGenerated,
  });

  const renderCell = (col: { key: string; label: string }, row: Record<string, unknown>) => {
    const val = row[col.key];
    const str = typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—');

    if (col.key === 'status' || col.key === 'compliance_status' || col.key === 'app_status') {
      return <Chip label={str} size="small" sx={{ ...statusChipSx(str, resolvedMode), fontSize: '0.72rem', height: 20 }} />;
    }
    if (col.key === 'risk_level') {
      return <Chip label={str} size="small" sx={{ ...riskChipSx(str, resolvedMode), fontSize: '0.72rem', height: 20 }} />;
    }
    const href =
      col.key === 'user' || col.key === 'delegate' || col.key === 'email'
        ? '/users'
        : col.key === 'application'
          ? '/applications'
          : null;
    if (href) {
      return (
        <Typography
          component={RouterLink}
          to={href}
          sx={{ fontSize: '0.82rem', color: 'primary.main', textDecoration: 'none' }}
        >
          {str}
        </Typography>
      );
    }
    return <Typography sx={{ fontSize: '0.82rem' }}>{str}</Typography>;
  };

  const [weeklyEmail, setWeeklyEmail] = useState(() => {
    try {
      return localStorage.getItem('provysn-weekly-report') === '1';
    } catch {
      return false;
    }
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>Reports &amp; Analytics</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Role-scoped insights from live application, user, and SSO data. Export any report as CSV or PDF.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
          sx={{ fontSize: '0.78rem', py: 0.5 }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warnings.join(' ')}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search reports…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /></InputAdornment>,
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.82rem' }}>Type</InputLabel>
              <Select value={filterType} onChange={e => setFilterType(e.target.value)} label="Type" sx={{ fontSize: '0.82rem' }}>
                <MenuItem value="all">All Reports</MenuItem>
                <MenuItem value="inactive_users">Inactive Users</MenuItem>
                <MenuItem value="all_inactive">All-Inactive Apps</MenuItem>
                <MenuItem value="sso_status">SSO Status</MenuItem>
                <MenuItem value="application_portfolio">Application Portfolio</MenuItem>
                <MenuItem value="delegations">Delegations</MenuItem>
                <MenuItem value="compliance">Compliance</MenuItem>
                <MenuItem value="security">Security</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <ListingSortSelect value={listingSort} onChange={setListingSort} />
          </Grid>
        </Grid>
      </Paper>

      {!loading && reports.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Reports', value: reports.length, icon: <AssessmentIcon />, color: colors.primary },
            { label: 'Total Records', value: reports.reduce((s, r) => s + r.data.length, 0), icon: <AppsIcon />, color: '#10b981' },
            {
              label: 'Inactive Owners',
              value: reports.find(r => r.id === 'inactive_users')?.data.length ?? 0,
              icon: <PeopleIcon />,
              color: '#f59e0b',
            },
            {
              label: 'Apps w/ No Coverage',
              value: reports.find(r => r.id === 'all_inactive')?.data.length ?? 0,
              icon: <WarningIcon />,
              color: '#ef4444',
            },
          ].map(stat => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>{stat.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(report => (
            <Grid item xs={12} md={6} lg={4} key={report.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: `4px solid ${report.color}`,
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ color: report.color, mt: 0.25 }}>{report.icon}</Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{report.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {report.data.length === 1 ? '1 record' : `${report.data.length} records`}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, flexGrow: 1, fontSize: '0.82rem' }}>
                  {report.description}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {report.columns.map((col) => (
                    <Chip key={col.key} label={col.label} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                  ))}
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {report.lastGenerated.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setSelected(report)}>
                      View
                    </Button>
                    <Tooltip title="Export CSV">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => exportCSV(report)}
                          disabled={report.data.length === 0}
                          sx={{ bgcolor: report.color, '&:hover': { bgcolor: report.color, filter: 'brightness(0.9)' } }}
                        >
                          CSV
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Export PDF (print dialog)">
                      <span>
                        <Button size="small" variant="outlined" onClick={() => exportPDF(report)} disabled={report.data.length === 0}>
                          PDF
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
          {filtered.length === 0 && !error && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No reports match your filters.</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="lg" fullWidth>
        {selected && (
          <>
            <DialogTitle
              sx={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.light || '#42a5f5'} 100%)`,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
              }}
            >
              <Box sx={{ color: 'white', display: 'flex' }}>{selected.icon}</Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{selected.title}</Typography>
                <Typography sx={{ fontSize: '0.78rem', opacity: 0.85 }}>{selected.description}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelected(null)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {selected.data.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No data for this report in your current scope.</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader sx={listingTableSx}>
                    <TableHead>
                      <TableRow>
                        {selected.columns.map(col => (
                          <TableCell key={col.key} sx={HEADER_SX}>{col.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortListing(selected.data, listingSort).map((row, i) => (
                        <TableRow key={i} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                          {selected.columns.map(col => (
                            <TableCell key={col.key} sx={{ py: 0.75 }}>
                              {renderCell(col, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                {selected.data.length} record{selected.data.length !== 1 ? 's' : ''}
                {weeklyEmail ? ' · Saved on this browser. No email is sent. Monday delivery to the IGA queue stays here until a mail service is connected.' : ' · A weekly subscribe does not send email.'}
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  const next = !weeklyEmail;
                  setWeeklyEmail(next);
                  try {
                    localStorage.setItem('provysn-weekly-report', next ? '1' : '0');
                  } catch {
                    /* ignore */
                  }
                }}
              >
                {weeklyEmail ? 'Weekly email saved here' : 'Subscribe weekly email'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  try {
                    localStorage.setItem('provysn-report-filter', JSON.stringify({
                      report: selected.title,
                      savedAt: new Date().toISOString(),
                    }));
                  } catch {
                    /* ignore */
                  }
                }}
              >
                Save this filter
              </Button>
              <Button size="small" onClick={() => setSelected(null)}>Close</Button>
              <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={() => exportCSV(selected)} disabled={selected.data.length === 0}>
                Export CSV
              </Button>
              <Button size="small" variant="outlined" onClick={() => exportPDF(selected)} disabled={selected.data.length === 0}>
                Export PDF
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Reports;
